import { BaseCommand } from './BaseCommand.js';
import * as fs from 'fs-extra';
import * as path from 'path';

export interface ScaffoldOptions {
	type: string;
	name?: string;
	template?: string;
	typescript?: boolean;
	test?: boolean;
}

export class ScaffoldCommand extends BaseCommand {
	async execute(options: ScaffoldOptions): Promise<void> {
		this.log(`Scaffolding ${options.type}${options.name ? ` "${options.name}"` : ''}...`);

		try {
			switch (options.type.toLowerCase()) {
				case 'component':
					await this.scaffoldComponent(options);
					break;
				case 'view':
				case 'page':
					await this.scaffoldView(options);
					break;
				case 'composable':
					await this.scaffoldComposable(options);
					break;
				case 'store':
					await this.scaffoldStore(options);
					break;
				case 'middleware':
					await this.scaffoldMiddleware(options);
					break;
				case 'layout':
					await this.scaffoldLayout(options);
					break;
				case 'plugin':
					await this.scaffoldPlugin(options);
					break;
				default:
					throw new Error(`Unknown scaffold type: ${options.type}`);
			}

			this.logSuccess(`${options.type} scaffolded successfully!`);

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			this.logError(`Failed to scaffold ${options.type}: ${errorMessage}`);
			throw error;
		}
	}

	private async scaffoldComponent(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'NewComponent';
		const componentName = this.toPascalCase(name);
		const fileName = `${componentName}.vue`;
		const componentPath = path.join(this.cwd, 'src', 'components', fileName);

		await fs.ensureDir(path.dirname(componentPath));

		const template = `<template lang="pug">
.${this.toKebabCase(componentName)}
  p ${componentName} component
</template>

<script setup${options.typescript ? ' lang="ts"' : ''}>
// ${componentName} component logic
${options.typescript ? 'interface Props {\n  // Define props here\n}\n\n// const props = defineProps<Props>()' : ''}
</script>

<style scoped>
.${this.toKebabCase(componentName)} {
  /* ${componentName} styles */
}
</style>
`;

		await fs.outputFile(componentPath, template);
		this.log(`Created component: ${componentPath}`);

		// Create test file if requested
		if (options.test) {
			await this.createTestFile(componentPath, componentName, 'component');
		}
	}

	private async scaffoldView(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'NewView';
		const viewName = this.toPascalCase(name);
		const fileName = `${viewName}.vue`;
		const viewPath = path.join(this.cwd, 'src', 'views', fileName);

		await fs.ensureDir(path.dirname(viewPath));

		const template = `<template lang="pug">
.${this.toKebabCase(viewName)}
  h1 ${viewName}
  p Welcome to the ${viewName} view
</template>

<script setup${options.typescript ? ' lang="ts"' : ''}>
import { useFrameMeta } from '#frame/meta-helpers'

// Set page meta
useFrameMeta({
  title: '${viewName}',
  description: '${viewName} page description'
})

// ${viewName} view logic here
</script>

<style scoped>
.${this.toKebabCase(viewName)} {
  padding: 2rem;
}

h1 {
  color: #42b883;
  margin-bottom: 1rem;
}
</style>
`;

		await fs.outputFile(viewPath, template);
		this.log(`Created view: ${viewPath}`);

		// Create test file if requested
		if (options.test) {
			await this.createTestFile(viewPath, viewName, 'view');
		}
	}

	private async scaffoldComposable(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'newComposable';
		const composableName = this.toCamelCase(name);
		const fileName = `${composableName}.ts`;
		const composablePath = path.join(this.cwd, 'src', 'composables', fileName);

		await fs.ensureDir(path.dirname(composablePath));

		const template = `import { ref, computed } from 'vue'

export function ${composableName}() {
  const state = ref(0)
  
  const doubleState = computed(() => state.value * 2)
  
  const increment = () => {
    state.value++
  }
  
  const decrement = () => {
    state.value--
  }
  
  return {
    state,
    doubleState,
    increment,
    decrement
  }
}
`;

		await fs.outputFile(composablePath, template);
		this.log(`Created composable: ${composablePath}`);

		// Create test file if requested
		if (options.test) {
			await this.createTestFile(composablePath, composableName, 'composable');
		}
	}

	private async scaffoldStore(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'newStore';
		const storeName = this.toCamelCase(name);
		const fileName = `${storeName}.ts`;
		const storePath = path.join(this.cwd, 'src', 'store', fileName);

		await fs.ensureDir(path.dirname(storePath));

		const template = `import { defineStore } from 'pinia'

export const use${this.toPascalCase(storeName)} = defineStore('${storeName}', {
  state: () => ({
    items: [] as any[],
    loading: false,
    error: null as string | null
  }),
  
  getters: {
    itemCount: (state) => state.items.length,
    hasItems: (state) => state.items.length > 0
  },
  
  actions: {
    async fetchItems() {
      this.loading = true
      this.error = null
      
      try {
        // Implement your fetch logic here
        // const response = await api.fetchItems()
        // this.items = response.data
        
        this.loading = false
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'An error occurred'
        this.loading = false
      }
    },
    
    addItem(item: any) {
      this.items.push(item)
    },
    
    removeItem(index: number) {
      this.items.splice(index, 1)
    },
    
    clearItems() {
      this.items = []
    }
  }
})
`;

		await fs.outputFile(storePath, template);
		this.log(`Created store: ${storePath}`);

		// Create test file if requested
		if (options.test) {
			await this.createTestFile(storePath, storeName, 'store');
		}
	}

	private async scaffoldMiddleware(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'newMiddleware';
		const middlewareName = this.toCamelCase(name);
		const fileName = `${middlewareName}.ts`;
		const middlewarePath = path.join(this.cwd, 'src', 'middleware', fileName);

		await fs.ensureDir(path.dirname(middlewarePath));

		const template = `import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'

export default function ${middlewareName}(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  // Implement your middleware logic here
  console.log('Middleware ${middlewareName} executed')
  console.log('From:', from.path)
  console.log('To:', to.path)
  
  // Allow navigation to continue
  next()
  
  // Or redirect/block navigation:
  // next('/login')
  // next(false)
}
`;

		await fs.outputFile(middlewarePath, template);
		this.log(`Created middleware: ${middlewarePath}`);
	}

	private async scaffoldLayout(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'NewLayout';
		const layoutName = this.toPascalCase(name);
		const fileName = `${layoutName}.vue`;
		const layoutPath = path.join(this.cwd, 'src', 'layouts', fileName);

		await fs.ensureDir(path.dirname(layoutPath));

		const template = `<template lang="pug">
.${this.toKebabCase(layoutName)}-layout
  header.header
    h1 ${layoutName} Layout
  
  main.main
    router-view
  
  footer.footer
    p © 2024 Frame App
</template>

<script setup${options.typescript ? ' lang="ts"' : ''}>
// ${layoutName} layout logic
</script>

<style scoped>
.${this.toKebabCase(layoutName)}-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.header {
  background-color: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.main {
  flex: 1;
  padding: 2rem;
}

.footer {
  background-color: #f8f9fa;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  text-align: center;
}
</style>
`;

		await fs.outputFile(layoutPath, template);
		this.log(`Created layout: ${layoutPath}`);
	}

	private async scaffoldPlugin(options: ScaffoldOptions): Promise<void> {
		const name = options.name || 'newPlugin';
		const pluginName = this.toCamelCase(name);
		const fileName = `${pluginName}.ts`;
		const pluginPath = path.join(this.cwd, 'src', 'plugins', fileName);

		await fs.ensureDir(path.dirname(pluginPath));

		const template = `import type { App } from 'vue'

export interface ${this.toPascalCase(pluginName)}Options {
  // Define plugin options here
}

export default {
  install(app: App, options: ${this.toPascalCase(pluginName)}Options = {}) {
    // Plugin installation logic
    console.log('${this.toPascalCase(pluginName)} plugin installed', options)
    
    // Example: Add global property
    app.config.globalProperties.$${pluginName} = {
      // Plugin methods
    }
    
    // Example: Add global component
    // app.component('${this.toPascalCase(pluginName)}', SomeComponent)
    
    // Example: Add global directive
    // app.directive('${pluginName}', someDirective)
  }
}
`;

		await fs.outputFile(pluginPath, template);
		this.log(`Created plugin: ${pluginPath}`);
	}

	private async createTestFile(filePath: string, name: string, type: string): Promise<void> {
		const ext = path.extname(filePath);
		const testFileName = path.basename(filePath, ext) + '.spec.ts';
		const testPath = path.join(this.cwd, 'tests', type + 's', testFileName);

		await fs.ensureDir(path.dirname(testPath));

		let template = '';

		if (type === 'component' || type === 'view') {
			template = `import { describe, it, expect } from 'vitest'
import { mount } from '@vue/test-utils'
import ${name} from '@/${type}s/${name}.vue'

describe('${name}', () => {
  it('renders properly', () => {
    const wrapper = mount(${name})
    expect(wrapper.text()).toContain('${name}')
  })
})
`;
		} else if (type === 'composable') {
			template = `import { describe, it, expect } from 'vitest'
import { ${name} } from '@/composables/${name}'

describe('${name}', () => {
  it('works correctly', () => {
    const { state, increment } = ${name}()
    
    expect(state.value).toBe(0)
    increment()
    expect(state.value).toBe(1)
  })
})
`;
		} else if (type === 'store') {
			template = `import { describe, it, expect, beforeEach } from 'vitest'
import { setActivePinia, createPinia } from 'pinia'
import { use${this.toPascalCase(name)} } from '@/store/${name}'

describe('${name} store', () => {
  beforeEach(() => {
    setActivePinia(createPinia())
  })

  it('initializes with correct state', () => {
    const store = use${this.toPascalCase(name)}()
    expect(store.items).toEqual([])
    expect(store.loading).toBe(false)
    expect(store.error).toBe(null)
  })

  it('adds items correctly', () => {
    const store = use${this.toPascalCase(name)}()
    store.addItem({ id: 1, name: 'Test' })
    expect(store.items).toHaveLength(1)
    expect(store.hasItems).toBe(true)
  })
})
`;
		}

		await fs.outputFile(testPath, template);
		this.log(`Created test file: ${testPath}`);
	}

	private toPascalCase(str: string): string {
		return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
			return index === 0 ? word.toLowerCase() : word.toUpperCase();
		}).replace(/\s+/g, '').replace(/^./, str => str.toUpperCase());
	}

	private toCamelCase(str: string): string {
		return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
			return index === 0 ? word.toLowerCase() : word.toUpperCase();
		}).replace(/\s+/g, '');
	}

	private toKebabCase(str: string): string {
		return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
	}
} 